/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { Category, CategoryImages, Shops } from '@infrab4a/connect';
import { MessageService } from 'primeng/api';
import { FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload';
import { FileControllerService } from 'src/app/admin-api';
import { ImageUploadComponent } from 'src/app/components/image-upload/image-upload.component';
import { ShopCategoryService } from 'src/app/connect-api/api/shop/shop-category.service';
import { getShopsArray } from 'src/app/connect-api/enums/ShopMap';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { loadImageNaturalSize } from 'src/app/utils/image-upload.util';
import {
  SHOP_CATEGORY_IMAGE_ACCEPT,
  SHOP_CATEGORY_IMAGE_MAX_FILE_SIZE,
  canCopyShopCategoryImageToShop,
  getShopCategoryImageConstraint,
  getShopCategoryImageConstraintHint,
  getShopCategoryImageFormatsInfo,
  getShopCategoryImageMarginInfo,
  getShopCategoryImageMaxSizeInfo
} from './shop-category-image-dimensions';
import {
  ShopCategoryImageField,
  areShopCategoryImagesUnchanged,
  copyBannerToImageFields,
  getMissingRequiredShopCategoryImages,
  getNonBrandCategoryMainImage,
  getRequiredShopCategoryImageFields,
  getShopCategoryMissingImagesWarning,
  isShopCategoryImageFieldRequired
} from './shop-category-image-requirements';

@Component({
  selector: 'app-shop-category-images',
  templateUrl: './shop-category-images.component.html',
  styleUrl: './shop-category-images.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ShopCategoryImagesComponent
  extends ImageUploadComponent<CategoryImages>
  implements OnInit, OnChanges, OnDestroy
{
  @ViewChildren(FileUpload) fileUploadElements: QueryList<FileUpload>;
  @Input({ required: true }) category: Category;
  @Input() permission = true;
  @Output() categoryUpdated = new EventEmitter<Partial<Category>>();

  readonly acceptedImageFormats = SHOP_CATEGORY_IMAGE_ACCEPT;
  readonly maxImageFileSize = SHOP_CATEGORY_IMAGE_MAX_FILE_SIZE;
  readonly dimensionMarginInfo = getShopCategoryImageMarginInfo();
  readonly supportedFormatsInfo = getShopCategoryImageFormatsInfo();
  readonly maxFileSizeInfo = getShopCategoryImageMaxSizeInfo();
  missingImagesWarning: string | null = null;
  saveDisabled = true;
  private formChangesSub?: Subscription;

  override form = new FormGroup({
    Glamshop: this.imageForm,
    mensmarket: this.imageForm
  });

  constructor(
    private shoppingCategoryService: ShopCategoryService,
    override router: Router,
    override fileService: FileControllerService,
    private messageService: MessageService
  ) {
    super(fileService, router);
  }

  async ngOnInit(): Promise<void> {
    this.startPage();
  }

  startPage() {
    this.form.patchValue(this.category.images || {});
    this.availableShops?.forEach((s) => {
      if (this.category.shops?.includes(s)) this.form.get(s).enable();
      else this.form.get(s).disable();
    });
    this.applyRequiredImageValidators();
    this.formChangesSub?.unsubscribe();
    this.formChangesSub = this.form.valueChanges.subscribe(() => {
      this.refreshMissingImagesWarning();
      this.updateSaveDisabled();
    });
    this.refreshMissingImagesWarning();
    this.updateSaveDisabled();
  }

  override ngOnDestroy(): void {
    this.formChangesSub?.unsubscribe();
    super.ngOnDestroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['category']) {
      this.startPage();
    }
  }

  async submit(): Promise<void> {
    this.form.markAllAsTouched();
    if (this.hasMissingRequiredImages()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulário inválido',
        detail: 'Verifique os campos obrigatórios'
      });
      return;
    }

    try {
      LoaderService.showLoader();
      const images = copyBannerToImageFields(
        this.form.getRawValue(),
        this.isBrandCategory
      ) as CategoryImages;
      this.category.images = images;
      if (!this.isBrandCategory) {
        this.category.image = getNonBrandCategoryMainImage(
          images,
          this.shops
        );
      }
      const { metadata, ...category } = this.category;
      await this.shoppingCategoryService.updateCategory(
        ShopCategoryService.categoryUpdateDTO(category)
      );

      await this.afterSubmit();
      this.refreshMissingImagesWarning();
      this.updateSaveDisabled();
      this.categoryUpdated.emit({
        ...this.category,
        images
      });
      this.messageService.add({
        severity: 'success',
        detail: `Categoria atualizada com sucesso!`,
        summary: 'Sucesso'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  getShop(shop: Shops) {
    return getShopsArray().find((s) => s.value === shop);
  }

  getOtherShop(shop: Shops) {
    return getShopsArray().find((s) => s.value !== shop);
  }

  getFileUploadElement(id: string) {
    return this.fileUploadElements.find((f) => f.name === id);
  }

  getDimensionHint(shop: string, field: string): string | undefined {
    return getShopCategoryImageConstraintHint(shop, field);
  }

  get isBrandCategory(): boolean {
    return !!this.category?.brandCategory;
  }

  isFieldRequired(field: ShopCategoryImageField): boolean {
    return isShopCategoryImageFieldRequired(this.isBrandCategory, field);
  }

  invalidControl(shop: string, field: ShopCategoryImageField): boolean {
    const control = this.form.get(shop)?.get(field);
    return (
      !!control && control.invalid && (control.dirty || control.touched)
    );
  }

  private applyRequiredImageValidators(): void {
    const requiredFields = getRequiredShopCategoryImageFields(
      this.isBrandCategory
    );
    const allFields: ShopCategoryImageField[] = ['image', 'brandBanner'];

    this.availableShops.forEach((shop) => {
      const group = this.form.get(shop);
      allFields.forEach((field) => {
        const control = group?.get(field);
        if (!control) {
          return;
        }

        if (group.enabled && requiredFields.includes(field)) {
          control.setValidators(Validators.required);
        } else {
          control.clearValidators();
        }
        control.updateValueAndValidity();
      });
    });
  }

  private hasMissingRequiredImages(): boolean {
    return (
      getMissingRequiredShopCategoryImages(
        this.shops,
        this.form.getRawValue(),
        this.isBrandCategory
      ).length > 0
    );
  }

  private refreshMissingImagesWarning(): void {
    this.missingImagesWarning = getShopCategoryMissingImagesWarning(
      this.form.getRawValue(),
      this.shops,
      this.isBrandCategory
    );
  }

  private updateSaveDisabled(): void {
    this.saveDisabled = areShopCategoryImagesUnchanged(
      this.category?.images,
      this.form.getRawValue(),
      this.shops
    );
  }

  async onShopImageUpload(
    event: FileUploadHandlerEvent,
    shop: string,
    field: 'image' | 'brandBanner'
  ): Promise<string> {
    const constraint = getShopCategoryImageConstraint(shop, field);
    const url = await this.onUpload(
      event,
      `${shop}.${field}`,
      this.getFileUploadElement(`${field}_${shop}`),
      undefined,
      false,
      constraint?.width,
      constraint?.height,
      constraint?.marginPercent
    );

    if (url && field === 'brandBanner') {
      this.form.get(shop)?.get('brandBannerMobile')?.setValue(url);
      if (!this.isBrandCategory) {
        this.form.get(shop)?.get('image')?.setValue(url);
      }
    }

    this.refreshMissingImagesWarning();
    this.updateSaveDisabled();
    return url;
  }

  async copyImages(field: string, shop: Shops): Promise<void> {
    const image = this.form.get(shop).get(field).value;
    if (!image) {
      return;
    }

    const targetShops = this.shops.filter(
      (targetShop) =>
        targetShop !== shop && this.form.get(targetShop)?.get(field)?.enabled
    );
    const needsDimensionCheck = targetShops.some((targetShop) =>
      getShopCategoryImageConstraint(targetShop, field)
    );

    let imageSize: { width: number; height: number } | undefined;
    if (needsDimensionCheck) {
      LoaderService.showLoader();
      try {
        imageSize = await loadImageNaturalSize(image);
      } catch {
        AppDialogService.showErrorDialog({
          message: 'Não foi possível ler a imagem selecionada.'
        });
        return;
      } finally {
        LoaderService.showLoader(false);
      }
    }

    let refusedConstrainedCopy = false;
    this.shops.forEach((targetShop) => {
      if (!this.form.get(targetShop).get(field).enabled) {
        return;
      }

      if (
        targetShop !== shop &&
        !canCopyShopCategoryImageToShop(field, targetShop, imageSize)
      ) {
        refusedConstrainedCopy = true;
        return;
      }

      this.applyCopiedShopImage(field, targetShop, image);
    });

    if (refusedConstrainedCopy) {
      AppDialogService.showErrorDialog({
        message: 'A imagem selecionada não possui as dimensões solicitadas.'
      });
    }

    this.refreshMissingImagesWarning();
    this.updateSaveDisabled();
  }

  private applyCopiedShopImage(
    field: string,
    targetShop: string,
    image: string
  ): void {
    if (
      this.form.get(targetShop).get(field).value &&
      this.form.get(targetShop).get(field).value !== image
    ) {
      this.imagesReplaced.push(this.form.get(targetShop).get(field).value);
    }
    this.form.get(targetShop).get(field).setValue(image);
    if (field === 'brandBanner') {
      this.form.get(targetShop).get('brandBannerMobile')?.setValue(image);
      if (!this.isBrandCategory) {
        this.form.get(targetShop).get('image')?.setValue(image);
      }
    }
  }

  imageDiff(field: string) {
    return (
      this.shops.some(
        (s) =>
          this.category.shops?.includes(s) && !this.form.get(s).get(field).value
      ) ||
      this.shops.reduce((list, s) => {
        if (!list.includes(this.form.get(s).get(field).value))
          list.push(this.form.get(s).get(field).value);
        return list;
      }, []).length !== 1
    );
  }

  public override get filePath(): string {
    return '/admin/shop/products/categories/';
  }

  get defaultImg(): string {
    return 'assets/images/no_img_available.jpg';
  }

  override get model() {
    return this.category.images;
  }

  override get modelId() {
    return Number(this.category.id);
  }

  get imageForm() {
    return new FormGroup({
      image: new FormControl<string>(null),
      brandBanner: new FormControl<string>(null),
      brandBannerMobile: new FormControl<string>(null),
      sameImage: new FormControl(false),
      sameBrandBanner: new FormControl(false),
      sameBrandBannerMobile: new FormControl(false)
    });
  }

  get availableShops() {
    return Object.keys(this.form.controls);
  }

  get shops() {
    return this.availableShops.filter((s) => this.form.get(s).enabled);
  }
}
